import { useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';
import { PLAN_TERMS, type CreateSessionResult } from '@wallflower/shared';
import { api } from '../lib/api.ts';
import CopyField from '../components/CopyField.tsx';
import PaymentDialog from '../components/PaymentDialog.tsx';
import { formatCountdown, useCountdown } from '../lib/useCountdown.ts';
import { useDocumentTitle } from '../lib/useDocumentTitle.ts';

export default function Created() {
  const { state } = useLocation();
  // `name` is not part of the API result; the dashboard adds it to the state.
  const result = state as (CreateSessionResult & { name?: string }) | null;
  useDocumentTitle('Your links');
  const isDemo = result?.plan === 'demo';
  // Paid stays paid even though `result` is frozen navigation state.
  const [paid, setPaid] = useState(false);
  const [paying, setPaying] = useState(false);
  const isUnpaid = result?.plan === 'unpaid' && !paid;
  const terms = PLAN_TERMS[result?.plan ?? 'unpaid'];
  const remaining = useCountdown(isDemo ? result?.expiresAt : null);

  if (!result?.adminToken) return <Navigate to="/" replace />;

  return (
    <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col justify-center px-6 py-16">
      <p className="eyebrow">Ready to go</p>
      <h1 className="font-display mt-3 text-4xl font-semibold sm:text-5xl">
        Three links, one event.
      </h1>
      <p className="mt-4 text-muted">
        Save the control panel link somewhere safe. It is the only key to this slideshow, and
        anyone who has it can moderate the screen.
      </p>

      {isDemo ? (
        <div className="panel mt-6 p-5">
          <div className="flex items-baseline justify-between gap-4">
            <span className="eyebrow">Demo wall</span>
            <span className="font-display text-2xl text-sulphur">
              {remaining !== null ? formatCountdown(remaining) : ''}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted">
            {remaining === 0
              ? 'This demo wall has ended. Everything on it has been deleted.'
              : `Everything works, on a short lead: this wall and everything on it is deleted when the
                 clock runs out. It holds ${result.limits.media} photos and ${result.limits.messages} messages, and video is off.`}
          </p>
        </div>
      ) : null}

      {/* An unpaid wall has real limits of zero, so say so before a host shares the QR. */}
      {isUnpaid ? (
        <div className="panel mt-6 p-5">
          <span className="eyebrow">{terms.label}</span>
          <p className="mt-2 text-sm text-muted">
            The links below already work, but guests cannot add anything until this wall is paid
            for. Nothing is lost in the meantime.
          </p>
          <button type="button" className="btn btn-primary mt-4" onClick={() => setPaying(true)}>
            Pay to go live
          </button>
        </div>
      ) : null}

      {paid ? (
        <div className="panel mt-6 p-5">
          <span className="eyebrow">{PLAN_TERMS.pro.label}</span>
          <p className="mt-2 text-sm text-muted">
            Payment received - the wall goes live the moment Stripe confirms it, usually a few
            seconds. Guests can then add photos straight away.
          </p>
        </div>
      ) : null}

      {result.plan !== 'demo' ? (
        <p className="mt-6 text-sm text-muted">
          Saved to your account -{' '}
          <Link to="/dashboard" className="text-petal hover:underline">
            see all your events
          </Link>
          .
        </p>
      ) : null}

      <div className="mt-10 grid gap-4">
        <CopyField
          label="Open on the TV"
          hint="Full screen this in a browser on the screen your guests will see."
          value={result.slideshowUrl}
        />
        <CopyField
          label="Your control panel"
          hint="Keep this one private. Bookmark it on your phone."
          value={result.adminUrl}
          tone="warn"
        />
        <CopyField
          label="Guest upload page"
          hint="Guests reach this by scanning the QR code, so you rarely need it."
          value={result.uploadUrl}
        />
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <a className="btn btn-primary" href={result.adminUrl}>
          Go to the control panel
        </a>
        <a className="btn btn-ghost" href={result.slideshowUrl} target="_blank" rel="noreferrer">
          Preview the slideshow
        </a>
        <Link className="btn btn-ghost" to="/">
          Start another
        </Link>
      </div>

      {paying ? (
        <PaymentDialog
          wallName={result.name ?? 'this wall'}
          start={() => api.adminCheckout(result.adminToken)}
          onPaid={() => {
            setPaying(false);
            setPaid(true);
          }}
          onClose={() => setPaying(false)}
        />
      ) : null}
    </main>
  );
}
